import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import { fetchParishes } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';

interface Parish {
  id: number;
  name: string;
  location: string;
  archdeaconry: string;
  slug: string;
  photo?: string;
}

const Parishes = () => {
  const [parishes, setParishes] = useState<Parish[]>([]);
  const [filteredParishes, setFilteredParishes] = useState<Parish[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterArchdeaconry, setFilterArchdeaconry] = useState('all');
  const [expandedArchdeaconries, setExpandedArchdeaconries] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadParishes = async () => {
      try {
        const response = await fetchParishes();
        const transformed = response.data.parishes.map((item: any) => ({
          id: item.id,
          name: item.name,
          location: item.location ?? 'SIAMAC',
          archdeaconry: item.archdeaconry?.name ?? 'SIAMAC',
          slug: item.slug,
          photo: item?.photo
        }));
        setParishes(transformed);
        setFilteredParishes(transformed);
      } catch (error) {
        console.error('Error fetching parishes:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch parishes.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadParishes();
  }, [toast]);

  useEffect(() => {
    const filtered = parishes.filter(parish => {
      const matchesSearch = parish.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parish.location.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesArch = filterArchdeaconry === 'all' || parish.archdeaconry === filterArchdeaconry;
      return matchesSearch && matchesArch;
    });

    setFilteredParishes(filtered);
  }, [parishes, searchTerm, filterArchdeaconry]);

  const archdeaconries = [...new Set(parishes.map(p => p.archdeaconry))];

  useEffect(() => {
    setExpandedArchdeaconries(prev => {
      const updated: Record<string, boolean> = {};
      archdeaconries.forEach(name => {
        updated[name] = prev[name] !== undefined ? prev[name] : true;
      });
      return updated;
    });
  }, [archdeaconries]);

  const groupedParishes = filteredParishes.reduce((acc, parish) => {
    if (!acc[parish.archdeaconry]) acc[parish.archdeaconry] = [];
    acc[parish.archdeaconry].push(parish);
    return acc;
  }, {} as Record<string, Parish[]>);

  const toggleExpand = (archdeaconry: string) => {
    setExpandedArchdeaconries(prev => ({
      ...prev,
      [archdeaconry]: !prev[archdeaconry],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading parish information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-2">
            <Search className="h-5 w-5 text-gray-500" />
            <Input
              placeholder="Search by name or location"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full sm:w-80"
            />
          </div>
          <Select onValueChange={setFilterArchdeaconry} value={filterArchdeaconry}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Archdeaconry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Archdeaconries</SelectItem>
              {archdeaconries.map(a => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {Object.entries(groupedParishes).map(([archdeaconry, parishes]) => {
          const isExpanded = expandedArchdeaconries[archdeaconry];
          return (
            <div key={archdeaconry} className="mb-10">
              <div
                className="flex items-center justify-between cursor-pointer bg-white p-3 rounded shadow-sm border"
                onClick={() => toggleExpand(archdeaconry)}
              >
                <h2 className="text-lg font-bold text-gray-700">{archdeaconry}</h2>
                {isExpanded ? (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-500" />
                )}
              </div>

              {isExpanded && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {parishes.map(parish => (
                    <Card key={parish.id}>
                      <CardHeader>
                        <CardTitle>
                          <Link to={`/parishes/${parish.slug}`}>{parish.name}</Link>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {parish.photo && (
                          <img
                            src={parish.photo}
                            alt={parish.name}
                            className="mt-3 w-full h-48 object-cover rounded-md"
                          />
                        )}
                        <p><strong>Location:</strong> {parish.location}</p>
                        {parish.archdeaconry != 'SIAMAC' && <p><strong>Archdeaconry:</strong> {parish.archdeaconry}</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Parishes;
